import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import CocacolaLogo from "./OrgIndiaLogo";
import "../../styles/layout/SidebarStyles.css";
import { OrgIndiaLogo } from "./OrgIndiaLogo";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

export default function Sidebar({ routes, onMinimizeChange }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [openItems, setOpenItems] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Persist sidebar collapsed state
  useEffect(() => {
    const storage = localStorage.getItem("authToken")
      ? localStorage
      : sessionStorage;

    const name = storage.getItem("userName") || "Admin-User";
    const email = storage.getItem("email") || "no-email";

    setUserData({ name, email });
    setLoading(false);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("sidebarExpanded");
    if (saved !== null) {
      setIsExpanded(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarExpanded", JSON.stringify(isExpanded));
    if (onMinimizeChange) {
      onMinimizeChange(!isExpanded);
    }
  }, [isExpanded, onMinimizeChange]);

  // Toggle submenu
  const toggleItem = (stateKey) => {
    if (!stateKey) return;
    setOpenItems((prev) => ({
      ...prev,
      [stateKey]: !prev[stateKey],
    }));
  };

  // Check if route (or its children) is active
  const isRouteActive = (route) => {
    const fullPath = route.layout + route.path;
    if (location.pathname === fullPath) return true;
    if (route.views) {
      return route.views.some(
        (child) => location.pathname === child.layout + child.path
      );
    }
    return location.pathname.startsWith(fullPath);
  };

  const navItemStyles = {
    activeBg: "#C41E3A",
    activeHoverBg: "#8B0000",
    inactiveHoverBg: "whiteAlpha.100",
  };


  const renderNavItem = (route, index) => {
    if (
      route.redirect ||
      route.category === "inventory" ||
      route.category === "account"
    ) {
      return null;
    }

    const hasChildren = route.views && route.views.length > 0;
    const isOpen = openItems[route.state || ""];
    const active = isRouteActive(route);

    const handleParentClick = (e) => {
      if (hasChildren && route.state) {
        e.preventDefault();
        toggleItem(route.state);
      }
    };

    if (route.isExternal) {
      const active = false;

      return (
        <Box key={index} mb={1}>
          <a
            href={route.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <MotionBox
              layout
              whileHover={{ x: isExpanded ? 6 : 0 }}
              whileTap={{ scale: 0.98 }}
              cursor="pointer"
              borderRadius="14px"
              overflow="hidden"
            >
              <Flex
                align="center"
                justify={isExpanded ? "space-between" : "center"}
                px={isExpanded ? 5 : 2}
                py={3}
                bg="transparent"
                color="gray.300"
                transition="all 0.22s"
                _hover={{
                  bg: navItemStyles.activeBg,
                  color: "white",
                }}
              >
                <HStack spacing={isExpanded ? 3 : 0}>
                  <Box
                    p={2}
                    bg="whiteAlpha.200"
                    color="white"
                    borderRadius="10px"
                    minW="40px"
                    minH="40px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.2s"
                    _hover={{
                      bg: "white",
                      color: navItemStyles.activeBg,
                    }}
                  >
                    {route.icon}
                  </Box>

                  {isExpanded && (
                    <Text fontSize="sm" fontWeight="medium">
                      {route.name}
                    </Text>
                  )}
                </HStack>
              </Flex>
            </MotionBox>
          </a>
        </Box>
      );
    }


    return (

      <Box key={index} mb={1}>
        <NavLink to={route.layout + route.path} onClick={handleParentClick} end>
          <MotionBox
            layout
            whileHover={{ x: isExpanded ? 6 : 0 }}
            whileTap={{ scale: 0.98 }}
            cursor="pointer"
            borderRadius="14px"
            overflow="hidden"
          >
            <Flex
              align="center"
              justify={isExpanded ? "space-between" : "center"}
              px={isExpanded ? 4 : 2}
              py={3}
              bg={active ? "#C41E3A" : "transparent"}
              color={active ? "white" : "gray.300"}
              _hover={{
                bg: active ? "#8B0000" : "whiteAlpha.100",
              }}
              transition="all 0.22s"
            >
              <HStack spacing={isExpanded ? 3 : 0}>
                <Box
                  p={2}
                  bg={active ? "white" : "whiteAlpha.200"}
                  color={active ? "#C41E3A" : "white"}
                  borderRadius="10px"
                  transition="all 0.2s"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minW="40px"
                  minH="40px"
                >
                  {route.icon}
                </Box>

                <AnimatePresence>
                  {isExpanded && (
                    <MotionBox
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Text
                        fontWeight={active ? "bold" : "medium"}
                        fontSize="sm"
                        whiteSpace="nowrap"
                      >
                        {document.documentElement.dir === "rtl"
                          ? route.rtlName || route.name
                          : route.name}
                      </Text>
                    </MotionBox>
                  )}
                </AnimatePresence>
              </HStack>

              {isExpanded && hasChildren && (
                <MotionBox
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown size={18} />
                </MotionBox>
              )}
            </Flex>
          </MotionBox>
        </NavLink>

        {/* Children Submenu */}
        <AnimatePresence>
          {hasChildren && isOpen && isExpanded && (
            <MotionBox
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              overflow="hidden"
            >
              <VStack align="start" spacing={1} pl={10} py={2}>
                {route.views.map((child, i) => {
                  const childActive =
                    location.pathname === child.layout + child.path;

                  return (
                    <NavLink key={i} to={child.layout + child.path}>
                      <MotionBox
                        whileHover={{ x: 8 }}
                        whileTap={{ scale: 0.98 }}
                        w="full"
                      >
                        <Flex
                          align="center"
                          px={4}
                          py={2.5}
                          borderRadius="12px"
                          bg={childActive ? "#C41E3A" : "transparent"}
                          color={childActive ? "white" : "gray.400"}
                          _hover={{
                            bg: childActive ? "#8B0000" : "whiteAlpha.100",
                          }}
                        >
                          <Box
                            p={1.5}
                            bg={childActive ? "white" : "whiteAlpha.200"}
                            color={childActive ? "#C41E3A" : "white"}
                            borderRadius="8px"
                          >
                            {child.icon}
                          </Box>
                          <Text
                            ml={3}
                            fontSize="sm"
                            fontWeight={childActive ? "bold" : "medium"}
                          >
                            {document.documentElement.dir === "rtl"
                              ? child.rtlName || child.name
                              : child.name}
                          </Text>
                        </Flex>
                      </MotionBox>
                    </NavLink>
                  );
                })}
              </VStack>
            </MotionBox>
          )}
        </AnimatePresence>
      </Box>
    );
  };

  const sidebarContent = (
    <Flex
      direction="column"
      h="100vh"
      bg="gray.900"
      color="white"
      width={isExpanded ? "280px" : "80px"}
      transition="width 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
      overflow="hidden"
      boxShadow="2xl"
      position="relative"
    >
      {/* Header */}
      <Flex
        align="center"
        justify={isExpanded ? "space-between" : "center"}
        p={isExpanded ? 5 : 4}
        h="80px"
        borderBottom="1px solid"
        borderColor="gray.800"
        position="relative"
      >
        <MotionFlex layout align="center" gap={3}>
          <CocacolaLogo isExpanded={isExpanded} />
        </MotionFlex>

        {/* Close button inside drawer for mobile */}
        {isMobile && isOpen && (
          <DrawerCloseButton
            position="absolute"
            right={3}
            top="50%"
            transform="translateY(-50%)"
            color="white"
            size="lg"
          />
        )}
      </Flex>

      {/* Navigation Links */}
      <Box
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        px={2}
        py={4}
        css={{
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.2)",
            borderRadius: "3px",
          },
        }}
      >
        <VStack align="stretch" spacing={1}>
          {routes.map((route, i) => renderNavItem(route, i))}
        </VStack>
      </Box>
      <OrgIndiaLogo isExpanded={isExpanded} />
    </Flex>
  );

  // Mobile Version - Shows hamburger menu
  if (isMobile) {
    return (
      <>
        {/* Mobile Hamburger Button - Top Left */}
        <IconButton
          aria-label="Open menu"
          icon={<Menu size={24} />}
          position="fixed"
          left={4}
          top={4}
          zIndex={9999}
          bg="#F40009"
          color="white"
          shadow="2xl"
          borderRadius="full"
          width="44px"
          height="44px"
          onClick={onOpen}
          _hover={{ bg: "#C41E3A" }}
          _active={{ bg: "#8B0000" }}
        />

        <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
          <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
          <DrawerContent bg="gray.900" maxW="280px" margin={0} boxShadow="2xl">
            <DrawerBody p={0}>{sidebarContent}</DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // Desktop Version - Shows toggle arrow
  return (
    <>
      {/* Desktop Sidebar Container */}
      <Box position="fixed" left={0} top={0} h="100vh" zIndex={1000}>
        {sidebarContent}

        {/* Desktop Toggle Button - Positioned outside sidebar */}
        <IconButton
          icon={
            <MotionBox
              animate={{ rotate: isExpanded ? 0 : 180 }}
              transition={{ duration: 0.3 }}
            >
              {isExpanded ? (
                <ChevronLeft size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </MotionBox>
          }
          size="md"
          variant="solid"
          bg="#F40009"
          color="white"
          _hover={{ bg: "#C41E3A" }}
          _active={{ bg: "#8B0000" }}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label="Toggle sidebar"
          position="absolute"
          right="-20px"
          top="25px"
          zIndex={1001}
          borderRadius="full"
          boxShadow="2xl"
          width="32px"
          height="32px"
          minW="32px"
        />
      </Box>

      {/* Spacer for main content */}
      <Box
        w={isExpanded ? "280px" : "80px"}
        transition="width 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
        flexShrink={0}
      />
    </>
  );
}
